import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapTenderOpeningList } from '../utils/tenderOpeningHelpers';

const buildTenderOpeningListUrl = ({
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  tenderOpeningStatus,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));

  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }

  if (search?.trim()) {
    params.set('Search', search.trim());
  }

  if (
    tenderOpeningStatus !== '' &&
    tenderOpeningStatus !== null &&
    tenderOpeningStatus !== undefined
  ) {
    params.set('TenderOpeningStatus', String(tenderOpeningStatus));
  }

  if (sortField && sortField !== 'updateDate') {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }

  return `${api.API_URL}/procurement/get-all-tender-opening?${params.toString()}`;
};

const useTenderOpeningList = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
}) => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [tenderOpeningStatusFilter, setTenderOpeningStatusFilter] =
    useState('');
  const [sortField, setSortField] = useState('procurementnumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    void refreshTrigger;
    if (!enabled) return;

    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setIsLoading(true);

    try {
      const url = buildTenderOpeningListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        tenderOpeningStatus: tenderOpeningStatusFilter,
        sortField,
        sortDirection,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { items, totalItems } = unwrapTenderOpeningList(result);
      const sortedItems =
        sortField === 'updateDate'
          ? [...items].sort((a, b) => {
              const left = a.updateDateRaw || 0;
              const right = b.updateDateRaw || 0;
              return sortDirection === 'asc' ? left - right : right - left;
            })
          : items;

      setData({ items: sortedItems });
      setTotalRows(totalItems);
    } catch (error) {
      toast.error('Failed to load tender opening data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    tenderOpeningStatusFilter,
    sortField,
    sortDirection,
    procurementTypeHeader,
    enabled,
    refreshTrigger,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = e => setSearchText(e.target.value);

  const handleStatusFilter = value => {
    setTenderOpeningStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    const fieldMapping = {
      idProc: 'procurementnumber',
      submittedDate: 'submitteddatetenderopening',
      updateDate: 'updateDate',
      title: 'titleprocurement',
      period: 'contracttype',
      procType: 'procurementtype',
      status: 'tenderopeningstatus',
    };
    setSortField(fieldMapping[column.id] || column.id || 'procurementnumber');
    setSortDirection(direction);
  };

  const clearFilters = () => {
    setSearchText('');
    setTenderOpeningStatusFilter('');
    setSortField('procurementnumber');
    setSortDirection('desc');
    setCurrentPage(1);
  };

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  return {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    tenderOpeningStatusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
    refresh: fetchData,
  };
};

export default useTenderOpeningList;
